import { useCallback, useRef, useState } from 'react';

import { Icon } from '@iconify/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import config from '@core/config';
import { sessionFetch } from '@core/api/sessionFetch';
import { useAppSelector } from '@core/store/hooks';
import { logger } from '@core/utils';

/**
 * Renders the markdown link `[label](upload://<uuid>)` emitted by tool-issued
 * upload widgets (e.g. `request_tech_audit_upload`) as a clickable button that
 * POSTs the selected file to `/api/uploads/<uuid>/`. On success, calls
 * `onUploadComplete(uuid)` so the parent chat can send a synthetic follow-up.
 */
export function UploadLinkWidget({ href, label, onUploadComplete }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [filename, setFilename] = useState(null);
  const csrfToken = useAppSelector((state) => state.auth?.csrfToken);

  const uploadUuid = href.replace(/^upload:\/\//, '').replace(/\/$/, '');

  const handleFileChosen = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setStatus('uploading');
      setError(null);
      setFilename(file.name);

      const formData = new FormData();
      formData.append('file', file);

      try {
        const resp = await sessionFetch(`${config.apiBaseUrl}/api/uploads/${uploadUuid}/`, {
          method: 'POST',
          csrfToken,
          body: formData,
        });
        if (!resp.ok) {
          const body = await resp.text();
          throw new Error(t('ai.uploadLink.failedWithStatus', { status: resp.status, body: body.slice(0, 200) }));
        }
        setStatus('success');
        onUploadComplete?.(uploadUuid, file.name);
      } catch (err) {
        logger.error('Upload widget failed:', err);
        setStatus('error');
        setError(err.message || t('ai.uploadLink.failed'));
      }
    },
    [csrfToken, uploadUuid, onUploadComplete, t],
  );

  const openPicker = useCallback(() => fileInputRef.current?.click(), []);

  if (status === 'success') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 my-1 rounded-md bg-green-50 text-green-700 text-sm">
        <Icon icon="mdi:check-circle" className="size-4" />
        {filename ? t('ai.uploadLink.uploadedFile', { filename }) : t('ai.uploadLink.uploaded')}
      </span>
    );
  }

  return (
    <span className="inline-flex flex-col gap-1 my-1">
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileChosen}
      />
      <button
        type="button"
        onClick={openPicker}
        disabled={status === 'uploading'}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm cursor-pointer hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <Icon
          icon={status === 'uploading' ? 'mdi:loading' : 'mdi:cloud-upload-outline'}
          className={`size-4 ${status === 'uploading' ? 'animate-spin' : ''}`}
        />
        {status === 'uploading'
          ? t('ai.uploadLink.uploading', { filename: filename || '' })
          : (label || t('ai.uploadLink.uploadFile'))}
      </button>
      {status === 'error' && (
        <span className="text-xs text-red-600">{error}</span>
      )}
    </span>
  );
}

UploadLinkWidget.propTypes = {
  href: PropTypes.string.isRequired,
  label: PropTypes.string,
  onUploadComplete: PropTypes.func,
};
